import { ActivePromises } from '../Utility/ActivePromises';
import { Log } from '../AuxillaryService/Log';
import { LocalCache } from '../Cache/LocalCache';
import { $ } from '../../vendor/jquery';

/**
 * Successful Response
 *
 * Returns 3 values:
 *
 * 1. {Object} data
 * 2. {string} textStatus
 * 3. {jQuery.jqXHR} jqXHR
 * @typedef {jQuery.Promise} jqpSuccess
 */

/**
 * Response for HTTP or Cache error
 *
 * Returns 5 values:
 *
 * 1. {jQuery.jqXHR} jqXHR
 * 2. {string} textStatus
 * 3. {string} errorThrown
 * 4. {string} msg
 * 5. {Event} event
 * @typedef {jQuery.Promise} jqpFailure
 */

/**
 * @typedef {Object} ReachApiResponse
 * @property {*} data
 * @property {string} textStatus
 * @property {jQuery.jqXHR} jqXHR
 */

/**
 * @typedef {Error} ReachApiError
 * @property {*} data
 */

/**
 * @class
 * @hideconstructor
 */
function ReachAPI() {}

// hack so typescript knows what will be on this object
ReachAPI.ads = undefined;
ReachAPI.announcement = undefined;
ReachAPI.broadsign = undefined;
ReachAPI.cdn = undefined;
ReachAPI.data = undefined;
ReachAPI.display = undefined;
ReachAPI.font = undefined;
ReachAPI.hivestack = undefined;
ReachAPI.media = undefined;
ReachAPI.mq = undefined;
ReachAPI.player = undefined;
ReachAPI.placeexchange = undefined;
ReachAPI.powerbi = undefined;
ReachAPI.quotes = undefined;
ReachAPI.rss = undefined;
ReachAPI.scores = undefined;
ReachAPI.signage = undefined;
ReachAPI.socialmedia = undefined;
ReachAPI.stocks = undefined;
ReachAPI.vengo = undefined;
ReachAPI.vistar = undefined;
ReachAPI.widget = undefined;

var _get;

/**
 * API Server Host URL
 * @type {string}
 */
ReachAPI.server = ReachAPI.server || process.env.BASE_URL_API;

/**
 * Track active API promises (to prevent duplicate calls to the same api
 * endpoint before it's cached)
 * @type {ActivePromises}
 * @private
 */
var _activePromises = new ActivePromises('cache_id');

/**
 * AJAX Call
 * @param {('GET'|'POST'|'PUT'|'DELETE')} method
 * @param {string} url
 * @param {object} settings
 * @returns {jQuery.jqXHR}
 */
ReachAPI.ajax = function (method, url, settings) {
    settings = settings || {};

    settings.url = url;
    settings.method = method.toUpperCase();

    return $.ajax(settings);
};

/**
 * AJAX POST Call
 * @param {string} url
 * @param {Object} params
 * @returns {jQuery.jqXHR}
 */
ReachAPI.post = function (url, params) {
    return $.ajax({
        method: 'POST',
        data: params,
        url: url,
    });
};

/**
 * AJAX PUT Call
 * @param {string} url
 * @param {Object} params
 * @returns {jQuery.jqXHR}
 */
ReachAPI.put = function (url, params) {
    return $.ajax({
        method: 'PUT',
        data: params,
        url: url,
    });
};

/**
 * AJAX PATCH Call
 * @param {string} url
 * @param {Object} params
 * @returns {jQuery.jqXHR}
 */
ReachAPI.patch = function (url, params) {
    return $.ajax({
        method: 'PATCH',
        data: params,
        url: url,
    });
};

/**
 * AJAX GET Call
 * @param {string} url
 * @param {string} dataType
 * @param {Object} params
 * @returns {jQuery.jqXHR}
 */
ReachAPI.get = function (url, dataType, params) {
    return $.ajax({
        method: 'GET',
        data: params,
        cache: false,
        dataType: dataType,
        url: url,
    });
};

/**
 * AJAX GET Call
 * @param {string} url
 * @param {string} dataType
 * @param {Object} headers
 * @returns {jQuery.jqXHR}
 * @private
 */
_get = function (url, dataType, headers) {
    return $.ajax({
        method: 'GET',
        cache: false,
        dataType: dataType,
        url: url,
        headers: headers,
    });
};

/**
 * AJAX GET Call With Cached Fallback
 *
 * Try to retrieve data via an AJAX call.  If that fails try to retrieve data
 * from the cache.
 * @param {string} url
 * @param {string} dataType
 * @param {Object} [params]
 * @param {Object | undefined} headers
 * @param {string} [cache_id=url]
 * @throws {jqpFailure}
 * @returns {jqpSuccess}
 */
ReachAPI.getWithFallback = function (url, dataType, params, cache_id, headers = undefined) {
    url = ReachAPI.urlWithParams(url, params);
    cache_id = cache_id || url;

    var activePromiseItem = { cache_id: cache_id };

    var currentlyActiveItem = _activePromises.get(activePromiseItem);
    if (currentlyActiveItem) {
        return currentlyActiveItem.promise;
    }

    var jqDeferred = $.Deferred();
    var jqPromise = jqDeferred.promise();

    activePromiseItem.promise = jqPromise;
    _activePromises.add(activePromiseItem);

    _get(url, dataType, headers)
        .done(function (data, textStatus, jqXHR) {
            // got data from api

            // save to cache
            LocalCache.save(url, data, cache_id)
                .done(function () {
                    jqDeferred.resolve(data, textStatus, jqXHR);
                })
                .fail(function (msg, event) {
                    // fail
                    jqDeferred.reject(jqXHR, textStatus, null, msg, event);
                });
        })
        .fail(function (jqXHR, textStatus, errorThrown) {
            LocalCache.get(cache_id)
                .done(function (content) {
                    // got data from cache
                    jqDeferred.resolve(content, 'cache', jqXHR);
                })
                .fail(function (msg, event) {
                    // everything fails
                    jqDeferred.reject(jqXHR, textStatus, errorThrown, msg, event);
                });
        });

    return jqPromise;
};

/**
 * Cached GET Call with AJAX Fallback
 *
 * Try to retrieve data from the cache.  If that fails try to retrieve data via
 * an AJAX call.
 * @param {string} url
 * @param {string} dataType
 * @param {Object} [params]
 * @param {string} [cache_id=url]
 * @throws {jqpFailure}
 * @returns {jqpSuccess}
 */
ReachAPI.getCached = function (url, dataType, params, cache_id) {
    url = ReachAPI.urlWithParams(url, params);
    cache_id = cache_id || url;

    var activePromiseItem = { cache_id: cache_id };

    var currentlyActiveItem = _activePromises.get(activePromiseItem);
    if (currentlyActiveItem) {
        return currentlyActiveItem.promise;
    }

    var jqDeferred = $.Deferred();
    var jqPromise = jqDeferred.promise();

    activePromiseItem.promise = jqPromise;
    _activePromises.add(activePromiseItem);

    LocalCache.get(cache_id)
        .done(function (content) {
            // got data from cache
            jqDeferred.resolve(content, 'cache', null);
        })
        .fail(function (msg, event) {
            _get(url, dataType)
                .done(function (data, textStatus, jqXHR) {
                    // got data from api

                    // save to cache
                    LocalCache.save(url, data, cache_id)
                        .done(function () {
                            jqDeferred.resolve(data, textStatus, jqXHR);
                        })
                        .fail(function (msg, event) {
                            // fail
                            jqDeferred.reject(jqXHR, textStatus, null, msg, event);
                        });
                })
                .fail(function (jqXHR, textStatus, errorThrown) {
                    // everything fails
                    jqDeferred.reject(jqXHR, textStatus, errorThrown, msg, event);
                });
        });

    return jqPromise;
};

/**
 * Callback for updating data
 * @callback callbackUpdateData
 * @param {Object} oldData
 * @param {Object} newData
 * @return {Object} Merged Data
 */

/**
 * Cached GET Call with AJAX Update
 * Try to retrieve data from the cache and update via an AJAX call.
 * @param {string} url
 * @param {string} dataType
 * @param {Object} [params]
 * @param {string} [cache_id=url]
 * @param {callbackUpdateData} update_function
 * @throws {jqpFailure}
 * @returns {jqpSuccess}
 */
ReachAPI.getAndUpdateCached = function (url, dataType, params, cache_id, update_function) {
    url = ReachAPI.urlWithParams(url, params);
    cache_id = cache_id || url;

    var activePromiseItem = { cache_id: cache_id };

    var currentlyActiveItem = _activePromises.get(activePromiseItem);
    if (currentlyActiveItem) {
        return currentlyActiveItem.promise;
    }

    var jqDeferred = $.Deferred();
    var jqPromise = jqDeferred.promise();

    activePromiseItem.promise = jqPromise;
    _activePromises.add(activePromiseItem);

    var getDataFromAPI = _get(url, dataType);

    LocalCache.get(cache_id)
        .done(function (cached_content) {
            // got data from cache

            getDataFromAPI
                .done(function (api_content, textStatus, jqXHR) {
                    // got data from api

                    // merge cache and api data
                    var merged_content = update_function(cached_content, api_content);

                    // save to cache
                    LocalCache.save(url, merged_content, cache_id)
                        .done(function () {
                            // resolve merged
                            jqDeferred.resolve(merged_content, 'merge', jqXHR);
                        })
                        .fail(function (msg, event) {
                            // fail
                            jqDeferred.reject(jqXHR, textStatus, null, msg, event);
                        });
                })
                .fail(function () {
                    // api failed

                    // resolve cached
                    jqDeferred.resolve(cached_content, 'cache', null);
                });
        })
        .fail(function (msg, event) {
            // no data in cache

            getDataFromAPI
                .done(function (api_content, textStatus, jqXHR) {
                    // got data from api

                    // save to cache
                    LocalCache.save(url, api_content, cache_id)
                        .done(function () {
                            // resolve api
                            jqDeferred.resolve(api_content, textStatus, jqXHR);
                        })
                        .fail(function (msg, event) {
                            // fail
                            jqDeferred.reject(jqXHR, textStatus, null, msg, event);
                        });
                })
                .fail(function (jqXHR, textStatus) {
                    // everything fails
                    jqDeferred.reject(jqXHR, textStatus, null, msg, event);
                });
        });

    return jqPromise;
};

/**
 * Convert `ReachAPI` `jQuery.Promise` results to Promises/A+ compatible
 * promises
 * @param {jQuery.Deferred|jQuery.Promise} deferred
 * @throws {ReachApiError}
 * @returns {Promise<ReachApiResponse>}
 */
ReachAPI.deferredToPromise = function (deferred) {
    return new Promise(function (resolve, reject) {
        deferred
            .done(function (data, textStatus, jqXHR) {
                resolve({
                    data: data,
                    textStatus: textStatus,
                    jqXHR: jqXHR,
                });
            })
            .fail(function (jqXHR, textStatus, errorThrown, msg, event) {
                var theError = new Error('ReachAPI Call Failed');
                theError.data = ReachAPI.createPromiseFailObject(
                    jqXHR,
                    textStatus,
                    errorThrown,
                    msg,
                    event
                );
                reject(theError);
            });
    });
};

/**
 * Run AJAXed script with cached fallback
 *
 * The script is run in a *Self-Executing Anonymous Function* to prevent
 * cluttering the global space.  Some scripts may require re-writing to
 * accommodate this, if they need functions/objects added to the global space.
 *
 * @param {string} url
 * @param {Object} [params]
 * @param {string} [cache_id=url]
 * @throws {ReachApiError}
 * //returns {Promise<ReachApiResponse>}
 * @returns {Promise<ReachApiResponse>}
 */
ReachAPI.runScriptWithFallback = function (url, params, cache_id) {
    var jqDeferred = $.Deferred();
    var jqPromise = jqDeferred.promise();

    ReachAPI.getWithFallback(url, 'text', params, cache_id)
        .done(function (scriptText, textStatus, jqXHR) {
            if (textStatus === 'cache') {
                Log.Notice('Executing cached script: ' + url, null, __fileline);
            } else {
                Log.Notice('Executing script: ' + url, null, __fileline);
            }

            eval('(function() { ' + scriptText + '}.bind(window))();');

            Log.Notice('Finished executing script: ' + url, null, __fileline);

            jqDeferred.resolve(scriptText, textStatus, jqXHR);
        })
        .fail(function (jqXHR, textStatus, errorThrown, msg, event) {
            Log.Error(
                'Failed to run this script: ' + url,
                null,
                __fileline,
                ReachAPI.createPromiseFailObject(jqXHR, textStatus, errorThrown, msg, event)
            );

            jqDeferred.reject(jqXHR, textStatus, errorThrown, msg, event);
        });

    return jqPromise;
};

/**
 * Append Serialized Query String Parameters to the URL
 * @param {string} url
 * @param {Object} params
 * @returns {string}
 */
ReachAPI.urlWithParams = function (url, params) {
    var queryString;
    var sep;

    if (params) {
        queryString = $.param(params);

        if (queryString) {
            sep = url.indexOf('?') === -1 ? '?' : '&';
            url += sep + queryString;
        }
    }

    return url;
};

/**
 * @typedef {Object} PromiseFailObject
 * @property {Object} API
 * @property {jquery.jqXHR} API.jqXHR
 * @property {string} API.textStatus
 * @property {string} API.errorThrown
 * @property {Object} CACHE
 * @property {string} CACHE.message
 * @property {Event} CACHE.event
 */

/**
 * Create Promise Fail Object for Logging Purposes
 * @param {jQuery.jqXHR} jqXHR
 * @param {string} textStatus
 * @param {string} errorThrown
 * @param {?string} msg
 * @param {?Event} event
 * @returns {PromiseFailObject}
 */
ReachAPI.createPromiseFailObject = function (jqXHR, textStatus, errorThrown, msg, event) {
    return {
        API: {
            jqXHR: jqXHR,
            textStatus: textStatus,
            errorThrown: errorThrown,
        },
        CACHE: {
            message: msg,
            event: event,
        },
    };
};

/**
 * API Security Data
 * At least one of the parameters must be set.
 * @class
 * @todo: Refactor away
 * @param {string|null} [player_uuid]
 * @param {string|null} [security_token]
 */
ReachAPI.Security = function (player_uuid, security_token) {
    if (player_uuid) {
        this.player_uuid = player_uuid;
    } else if (security_token) {
        this.token = security_token;
    }
};

/**
 * Add security data to the parameters to be passed to the API
 * @param {object} params    parameters to be passed to the API
 * @returns {object}
 */
ReachAPI.Security.prototype.merge = function (params) {
    if (this.player_uuid) {
        params.player_uuid = this.player_uuid;
    } else if (this.token) {
        params.token = this.token;
    }

    return params;
};

export { ReachAPI };
